import React, { useReducer, useRef, useState, useEffect } from 'react'
import { get } from 'idb-keyval'

// CompetitorDataLogger - Allows salesmen to log competitor data during a customer visit.
// - Uses `useReducer` to manage form state for clarity and scalability.
// - Validates required fields and shelf share range.
// - Shows a loading spinner on submit and simulates an API POST.
// - Mobile-optimized layout with tap-friendly controls.

const initialState = {
  competitorName: '',
  productName: '',
  shelfShare: 0,
  price: '',
  promotions: [],
  notes: '',
  shopItem: '',
  shopItemName: '',
  photo: null,
  photoPreview: null,
  loading: false,
  success: false,
  errors: {},
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }
    case 'TOGGLE_PROMO': {
      const { value } = action
      const has = state.promotions.includes(value)
      return {
        ...state,
        promotions: has
          ? state.promotions.filter((p) => p !== value)
          : [...state.promotions, value],
      }
    }
    case 'SET_PHOTO':
      return { ...state, photo: action.photo, photoPreview: action.preview }
    case 'SET_LOADING':
      return { ...state, loading: action.value }
    case 'SET_SUCCESS':
      return { ...state, success: action.value }
    case 'SET_ERRORS':
      return { ...state, errors: action.errors }
    case 'RESET':
      return { ...initialState }
    default:
      return state
  }
}

export default function CompetitorDataLogger({ onCancel }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const fileInputRef = useRef(null)
  const [logs, setLogs] = useState([])
  const [shopItems, setShopItems] = useState([])

  useEffect(() => {
    try {
      const raw = localStorage.getItem('competitorLogs')
      if (raw) setLogs(JSON.parse(raw))
    } catch (e) {
      console.warn('Failed to load competitor logs', e)
    }

    // Load the shop's own items from IndexedDB (key 'items') populated during sync.
    ;(async () => {
      try {
        const stored = await get('items')
        if (Array.isArray(stored) && stored.length > 0) {
          const items = stored
            .map((it) => {
              if (!it) return null
              const id = it.id ?? it.product_id ?? it.productId
              const name = it.name || it.default_code || it.product_name || it.label
              return id === undefined || id === null || !name ? null : { id: id, name: String(name) }
            })
            .filter(Boolean)
          if (items.length) {
            items.sort((a, b) => String(a.name).localeCompare(String(b.name)))
            setShopItems(items)
          } else {
            setShopItems(fallbackShopItems)
          }
        } else {
          setShopItems(fallbackShopItems)
        }
      } catch (e) {
        console.warn('Failed to load shop items from IndexedDB', e)
        setShopItems(fallbackShopItems)
      }
    })()
  }, [])

  // Simple lists of competitor suggestions and the shop's own items.
  const competitorSuggestions = [
    'Competitor A',
    'Competitor B',
    'Competitor C',
    'Local Brand',
  ]

  const fallbackShopItems = [
    { id: 'basic-gadgets', name: 'Basic Gadgets' },
    { id: 'deluxe-gadgets', name: 'Deluxe Gadgets' },
    { id: 'economy-widgets', name: 'Economy Widgets' },
    { id: 'premium-widgets', name: 'Premium Widgets' },
    { id: 'standard-widgets', name: 'Standard Widgets' },
    { id: 'superior-supplies', name: 'Superior Supplies' },
  ]

  // Validate required fields; returns an errors object.
  function validate() {
    const errors = {}
    if (!state.competitorName || state.competitorName.trim() === '') {
      errors.competitorName = 'Competitor name is required.'
    }
    if (!state.productName || state.productName.trim() === '') {
      errors.productName = 'Competitor product name is required.'
    }
    if (state.price === '' || isNaN(Number(state.price))) {
      errors.price = 'Valid competitor price is required.'
    }
    const share = Number(state.shelfShare)
    if (Number.isNaN(share) || share < 0 || share > 100) {
      errors.shelfShare = 'Shelf share must be between 0 and 100.'
    }
    return errors
  }

  // Mock submit: logs payload and simulates a POST.
  function handleSubmit(e) {
    e.preventDefault()
    const errors = validate()
    dispatch({ type: 'SET_ERRORS', errors })
    if (Object.keys(errors).length > 0) return

    const payload = {
      competitorName: state.competitorName,
      productName: state.productName,
      shelfShare: Number(state.shelfShare),
      price: Number(state.price),
      promotions: state.promotions,
      notes: state.notes,
      shopItem: state.shopItem,
      shopItemName: state.shopItemName,
      // photo would be handled separately (FormData) in a real app
      hasPhoto: !!state.photo,
      timestamp: new Date().toISOString(),
    }

    // Show loading UI
    dispatch({ type: 'SET_LOADING', value: true })
    console.log('Submitting competitor data payload:', payload)

    // Simulate async POST
    setTimeout(() => {
      dispatch({ type: 'SET_LOADING', value: false })

      // Persist the entry locally so the user sees it in the "Logged Entries" list
      try {
        const entry = { ...payload, photoPreview: state.photoPreview }
        setLogs((prev) => {
          const updated = [entry, ...prev]
          try { localStorage.setItem('competitorLogs', JSON.stringify(updated)) } catch (e) { /* ignore */ }
          return updated
        })
      } catch (e) {
        console.warn('Failed to persist competitor log', e)
      }

      dispatch({ type: 'SET_SUCCESS', value: true })
      // Keep success message briefly then reset form
      setTimeout(() => {
        dispatch({ type: 'SET_SUCCESS', value: false })
        dispatch({ type: 'RESET' })
      }, 1800)
    }, 1000)
  }

  function handleFileChange(ev) {
    const file = ev.target.files && ev.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      dispatch({ type: 'SET_PHOTO', photo: file, preview: e.target.result })
    }
    reader.readAsDataURL(file)
  }

  function handleCancel() {
    // If parent provided onCancel, call it. Otherwise, fall back to browser history.
    if (typeof onCancel === 'function') return onCancel()
    window.history.back()
  }

  return (
    <div className="cdl-root" role="main">
      <header className="cdl-header">
        <button className="cdl-back" onClick={handleCancel} aria-label="Back to dashboard">
          ← Back
        </button>
        <h2>Log Competitor Data</h2>
      </header>

      <form className="cdl-form" onSubmit={handleSubmit}>
        <label className="cdl-label">
          Competitor Name*
          <input
            list="competitors"
            className="cdl-input"
            type="text"
            value={state.competitorName}
            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'competitorName', value: e.target.value })}
            placeholder="Start typing or select"
            aria-required="true"
          />
          <datalist id="competitors">
            {competitorSuggestions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          {state.errors.competitorName && <div className="cdl-error">{state.errors.competitorName}</div>}
        </label>

        <label className="cdl-label">
          Shop Item (Our Product)
          <select
            className="cdl-select"
            value={state.shopItem}
            onChange={(e) => {
              const selected = shopItems.find(
                (s) => String(s.id) === String(e.target.value)
              )
              dispatch({ type: 'SET_FIELD', field: 'shopItem', value: e.target.value })
              dispatch({
                type: 'SET_FIELD',
                field: 'shopItemName',
                value: selected ? selected.name : '',
              })
            }}
          >
            <option value="">Select shop item</option>
            {shopItems.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label className="cdl-label">
          Competitor Product Name*
          <input
            className="cdl-input"
            type="text"
            value={state.productName}
            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'productName', value: e.target.value })}
            placeholder="Product name"
            aria-required="true"
          />
          {state.errors.productName && <div className="cdl-error">{state.errors.productName}</div>}
        </label>

        <div className="cdl-row">
          <label className="cdl-label cdl-flex-1">
            Estimated Shelf Share %
            <input
              className="cdl-range"
              type="range"
              min="0"
              max="100"
              value={state.shelfShare}
              onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'shelfShare', value: e.target.value })}
            />
          </label>
          <label className="cdl-label cdl-narrow">
            <input
              className="cdl-input"
              type="number"
              min="0"
              max="100"
              value={state.shelfShare}
              onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'shelfShare', value: e.target.value })}
            />
            %
            {state.errors.shelfShare && <div className="cdl-error">{state.errors.shelfShare}</div>}
          </label>
        </div>

        <label className="cdl-label">
          Competitor Price*
          <input
            className="cdl-input"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={state.price}
            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'price', value: e.target.value })}
            placeholder="0.00"
            aria-required="true"
          />
          {state.errors.price && <div className="cdl-error">{state.errors.price}</div>}
        </label>

        <fieldset className="cdl-fieldset">
          <legend>Promotional Activity</legend>
          {['Price discount', 'Buy-1-Get-1', 'Special Display', 'None'].map((promo) => (
            <label key={promo} className="cdl-checkboxLabel">
              <input
                type="checkbox"
                checked={state.promotions.includes(promo)}
                onChange={() => dispatch({ type: 'TOGGLE_PROMO', value: promo })}
              />
              {promo}
            </label>
          ))}
        </fieldset>

        <label className="cdl-label">
          Notes / Observations
          <textarea
            className="cdl-textarea"
            value={state.notes}
            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'notes', value: e.target.value })}
            placeholder="Brief notes about placement, visibility, etc."
          />
        </label>

        <label className="cdl-label">
          Photo (Shelf image)
          <div className="cdl-photoRow">
            <button
              type="button"
              className="cdl-photoBtn"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              {state.photo ? 'Change Photo' : 'Upload / Capture Photo'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {state.photoPreview && (
              <img src={state.photoPreview} alt="preview" className="cdl-photoPreview" />
            )}
          </div>
        </label>

        <div className="cdl-actions">
          <button type="button" className="cdl-cancel" onClick={handleCancel}>
            Cancel
          </button>
          <button type="submit" className="cdl-submit" disabled={state.loading}>
            {state.loading ? (
              <span className="cdl-spinner" aria-hidden="true" />
            ) : (
              'Save Entry'
            )}
          </button>
        </div>

        {state.success && <div className="cdl-success">Saved successfully ✅</div>}
      </form>

      {logs.length > 0 && (
        <section className="cdl-logged" aria-live="polite">
          <h3 style={{ marginTop: 18 }}>Logged Entries</h3>
          <ul className="cdl-logList">
            {logs.map((l, i) => (
              <li key={l.timestamp || i} className="cdl-logItem">
                <div className="cdl-logRow">
                  {l.photoPreview ? (
                    <img src={l.photoPreview} alt="thumb" className="cdl-logThumb" />
                  ) : null}
                  <div className="cdl-logMeta">
                    <div>
                      <strong>{l.productName}</strong>
                      {l.competitorName ? ` — ${l.competitorName}` : ''}
                    </div>
                      <div className="cdl-muted">
                      {l.shopItemName || l.shopItem || '—'} • {l.price ? (l.price + ' ' + (l.currency || '')) : ''} • {l.shelfShare}%
                    </div>
                    <div className="cdl-logTime">{new Date(l.timestamp).toLocaleString()}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <style>{`
        .cdl-root { padding: 12px; max-width: 720px; margin: 0 auto; font-family: system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial; }
        .cdl-header { display:flex; align-items:center; gap:12px; margin-bottom:12px; }
        .cdl-back { background:none; border:none; color:#0078d4; font-size:16px; padding:8px; }
        .cdl-form { display:flex; flex-direction:column; gap:12px; }
        .cdl-label { display:flex; flex-direction:column; font-size:14px; }
        .cdl-input, .cdl-select, .cdl-textarea { font-size:16px; padding:12px; border-radius:8px; border:1px solid #ddd; width:100%; box-sizing:border-box; }
        .cdl-textarea { min-height:88px; resize:vertical; }
        .cdl-range { width:100%; accent-color:#0078d4; }
        .cdl-row { display:flex; gap:8px; align-items:center; }
        .cdl-flex-1 { flex:1; }
        .cdl-narrow { width:84px; display:flex; flex-direction:column; align-items:flex-end; }
        .cdl-fieldset { border:1px solid #eee; padding:8px; border-radius:8px; }
        .cdl-checkboxLabel { display:flex; gap:8px; align-items:center; margin:6px 0; }
        .cdl-photoRow { display:flex; gap:8px; align-items:center; }
        .cdl-photoBtn { padding:10px 14px; border-radius:8px; border:1px solid #ccc; background:#f8f8f8; }
        .cdl-photoPreview { width:64px; height:64px; object-fit:cover; border-radius:6px; border:1px solid #ddd; }
        .cdl-actions { display:flex; gap:12px; justify-content:space-between; margin-top:6px; }
        .cdl-cancel { flex:1; padding:12px; border-radius:10px; background:#fff; border:1px solid #ccc; }
        .cdl-submit { flex:2; padding:12px; border-radius:10px; background:#0078d4; color:white; border:none; font-weight:600; }
        .cdl-error { color:#b00020; font-size:13px; margin-top:6px; }
        .cdl-success { margin-top:12px; padding:10px; background:#e6ffed; border:1px solid #c0f0c6; border-radius:8px; color:#046b1b; font-weight:600; text-align:center; }
        .cdl-spinner { width:18px; height:18px; border:3px solid rgba(255,255,255,0.4); border-top-color:white; border-radius:50%; display:inline-block; animation:cdl-spin 800ms linear infinite; }
        @keyframes cdl-spin { to { transform: rotate(360deg); } }

        /* Mobile optimizations */
        @media (max-width: 640px) {
          .cdl-root { padding:16px; }
          .cdl-input, .cdl-select, .cdl-textarea { padding:14px; font-size:18px; }
          .cdl-photoPreview { width:72px; height:72px; }
          .cdl-back { font-size:18px; }
          .cdl-actions { flex-direction:column-reverse; }
          .cdl-submit, .cdl-cancel { width:100%; }
        }
        /* Logged entries list */
        .cdl-logged { margin-top: 18px; }
        .cdl-logList { list-style:none; padding:0; margin:8px 0 0 0; display:flex; flex-direction:column; gap:8px; }
        .cdl-logItem { background:#fff; border:1px solid #eee; border-radius:8px; padding:8px; }
        .cdl-logRow { display:flex; gap:8px; align-items:center; }
        .cdl-logThumb { width:48px; height:48px; object-fit:cover; border-radius:6px; }
        .cdl-logMeta { display:flex; flex-direction:column; gap:4px; }
        .cdl-muted { color:#666; font-size:13px; }
        .cdl-logTime { color:#888; font-size:12px; }
      `}</style>
    </div>
  )
}
